import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { getProductById } from "../firebase/productService";
import { getPricing } from "../firebase/pricingService";
import { useCart } from "../context/CartContext";
import { FRAME_TYPES, SIZES } from "../firebase/constants";
import img1 from "../assets/canvasPrint.webp";
import img2 from "../assets/stretchedCanvas.webp";
import img3 from "../assets/framedCanvas.webp";
import livingRoom from "../assets/livingRoom.jpg";
import bedroom from "../assets/bedroom.jpg";
import office from "../assets/office.jpg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@300;400;500&display=swap');
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

const FRAME_META = {
  "Canvas Print": {
    img: img1,
    desc: "Canvas prints do not include a stretcher bar or picture frame. Arrives rolled in tube packaging. Poly-cotton blend with a gloss finish — scratch, crack and warp resistant. Printed with UL Certified ink. Water resistant, anti-fade and anti-yellowing.",
  },
  "Stretched Canvas": {
    img: img2,
    desc: "Ready to hang. Each canvas comes standard with image-wrapped sides. Standard Gallery Wraps do not include a floating frame. Hand-stretched over solid pine bars.",
  },
  "Framed Canvas": {
    img: img3,
    desc: "Fully mounted and ready to hang. Hand crafted in Kosovo. Made from real pine wood with a professional matte black finish. Available in Black, White, Gold and Natural Wood.",
  },
};

/* ═══════════════════════════════════════════════════
   LAYOUT
═══════════════════════════════════════════════════ */
const Page = styled.div`
  min-height: 100vh;
  background: #080604;
  font-family: "Jost", sans-serif;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ImageSide = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  background: #0d0b08;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 22px 48px;
  box-sizing: border-box;

  @media (max-width: 900px) {
    position: relative;
    height: 75vw;
    min-height: 320px;
    padding: 40px 32px;
  }
`;

const ImageFrame = styled.div`
  width: 100%;
  height: 700px;
  display: flex;
  align-items: center;
  width: 90%;
  height: auto;
  max-height: 1000px;
  justify-content: center;
`;

const ProductImg = styled.img`
  width: 90%;
  height: auto;
  max-height: 1000px;
  object-fit: contain;
  display: block;
`;

const NoImage = styled.div`
  color: rgba(201, 169, 110, 0.15);
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
`;

const InfoSide = styled.div`
  padding: 126px 52px 72px;
  overflow-y: auto;
  animation: ${fadeIn} 0.5s ease forwards;

  @media (max-width: 1100px) {
    padding: 96px 36px 60px;
  }
  @media (max-width: 900px) {
    padding: 40px 24px 60px;
  }
`;

const Note = styled.p`
  font-size: 0.7rem;
  margin-bottom: 10px;
  font-weight: 300;
  color: #918f8f;
  font-family: "Montserrat";
`;

const BreadCrumb = styled.p`
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: #c9a96e;
  margin: 0 0 20px 0;
  cursor: pointer;
  transition: opacity 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    opacity: 0.7;
  }
`;

const ProductName = styled.h1`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(2.2rem, 4vw, 3.4rem);
  color: #f5ede0;
  letter-spacing: 0.01em;
  line-height: 1;
  margin: 0 0 6px 0;
`;

const CategoryTag = styled.span`
  display: inline-block;
  font-size: 0.58rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(201, 183, 150, 0.6);
  margin-bottom: 28px;
`;

const PriceRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 32px;
`;

const PriceValue = styled.span`
  font-family: "Cormorant Garamond", serif;
  font-size: 2.4rem;
  font-weight: 300;
  color: #f5ede0;
  letter-spacing: 0.02em;
  transition: all 0.2s ease;
`;

const PriceUnit = styled.span`
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(226, 225, 223, 0.3);
`;

const GoldRule = styled.div`
  height: 1px;
  background: linear-gradient(to right, rgba(201, 169, 110, 0.4), transparent);
  margin-bottom: 32px;
`;

const SectionLabel = styled.p`
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  margin: 0 0 14px 0;

  span {
    color: #c9a96e;
    margin-left: 8px;
  }
`;

const FrameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 32px;
  width: 150px;
  @media (max-width: 520px) {
    width: 100%;
  }
`;

const FrameCard = styled.button`
  background: ${(p) =>
    p.$active ? "rgba(201,169,110,0.1)" : "rgba(255,255,255,0.02)"};
  border: 1px solid ${(p) => (p.$active ? "#c9a96e" : "rgba(255,255,255,0.07)")};
  cursor: pointer;
  padding: 0;
  width: 150px;
  overflow: hidden;
  transition:
    border-color 0.25s,
    background 0.25s;
  text-align: center;
  @media (max-width: 520px) {
    width: 100%;
  }

  &:hover {
    border-color: rgba(201, 169, 110, 0.5);
    background: rgba(201, 169, 110, 0.06);
  }
`;

const FrameCardImg = styled.div`
  width: 150px;
  height: auto;
  overflow: hidden;
  background: #111;
  @media (max-width: 520px) {
    width: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${(p) => (p.$active ? 1 : 0.55)};
    transition: opacity 0.25s;
  }
`;

const FrameCardLabel = styled.div`
  padding: 8px 6px 10px;
  font-family: "Jost", sans-serif;
  font-size: 0.58rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${(p) => (p.$active ? "#c9a96e" : "rgba(245,237,224,0.4)")};
  transition: color 0.25s;
  line-height: 1.3;
`;

const SizePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 36px;
`;

const SizePill = styled.button`
  background: ${(p) => (p.$active ? "rgba(201,169,110,0.12)" : "transparent")};
  border: 1px solid ${(p) => (p.$active ? "#c9a96e" : "rgba(255,255,255,0.1)")};
  color: ${(p) => (p.$active ? "#c9a96e" : "rgba(245,237,224,0.45)")};
  font-family: "Jost", sans-serif;
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  padding: 9px 18px;
  cursor: pointer;
  border-radius: 999px;
  transition: all 0.22s ease;

  &:hover {
    border-color: rgba(201, 169, 110, 0.55);
    color: #c9a96e;
    background: rgba(201, 169, 110, 0.06);
  }
`;

const AddBtn = styled.button`
  width: 100%;
  background: #c9a96e;
  color: #080604;
  border: none;
  padding: 17px 32px;
  font-family: "Jost", sans-serif;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  align-self: self-start;
  cursor: pointer;
  transition:
    background 0.25s,
    transform 0.15s;
  margin-bottom: 12px;

  &:hover:not(:disabled) {
    background: #dbb97e;
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    background: rgba(201, 169, 110, 0.2);
    color: rgba(201, 169, 110, 0.4);
    cursor: not-allowed;
  }
`;

const AddedBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #6fcf97;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.35s ease;
  margin-bottom: 20px;
  height: 20px;
`;

const NoPriceNote = styled.p`
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  color: rgba(245, 237, 224, 0.2);
  text-align: center;
  margin: 0 0 20px 0;
`;

const DescSection = styled.div`
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DescCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DescTitle = styled.h4`
  font-family: "Jost", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #c9a96e;
  margin: 0;
`;

const DescText = styled.p`
  font-size: 0.8rem;
  line-height: 1.75;
  color: rgba(245, 237, 224, 0.4);
  margin: 0;
`;

const CenterWrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #080604;
`;

const LoadingDot = styled.span`
  width: 6px;
  height: 6px;
  background: rgba(201, 169, 110, 0.5);
  border-radius: 50%;
  display: inline-block;
  margin: 0 3px;
  animation: ${pulse} 1.2s ease infinite;
  animation-delay: ${(p) => p.$d || "0s"};
`;

/* ══════════════════════════════════════════════════
   ROOM PREVIEW — styled components
══════════════════════════════════════════════════ */
const RoomSection = styled.section`
  background: #050403;
  padding: 80px 5vw 100px;
  border-top: 1px solid rgba(201, 169, 110, 0.08);
`;

const RoomSectionHeader = styled.div`
  margin-bottom: 52px;
`;

const RoomEyebrow = styled.p`
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: #c9a96e;
  margin: 0 0 14px 0;
`;

const RoomTitle = styled.h2`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(2rem, 4vw, 3.2rem);
  color: #f5ede0;
  letter-spacing: 0.02em;
  line-height: 1;
  margin: 0 0 14px 0;
`;

const RoomSubtitle = styled.p`
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  color: rgba(245, 237, 224, 0.25);
  margin: 0;
`;

const RoomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

const RoomCard = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 4/3;
  background: #111;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }

  &:hover .room-label {
    opacity: 1;
    transform: translateY(0);
  }
`;

const RoomBg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: brightness(0.82);
  pointer-events: none;
`;

const ArtworkImg = styled.img`
  position: absolute;
  display: block;
  width: auto;
  max-height: 33%;
  max-width: 50%;
  object-fit: contain;
  border: 5px outset black;
  filter: brightness(0.8);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.55),
    0 2px 8px rgba(0, 0, 0, 0.4);
  cursor: grab;
  user-select: none;
  pointer-events: none; /* card handles all drag events */
  transition: box-shadow 0.2s ease;

  &.dragging {
    box-shadow:
      0 16px 48px rgba(0, 0, 0, 0.75),
      0 4px 16px rgba(0, 0, 0, 0.5);
  }
`;

const DragHint = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  font-family: "Jost", sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(245, 237, 224, 0.45);
  background: rgba(5, 4, 3, 0.55);
  backdrop-filter: blur(4px);
  padding: 4px 10px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${RoomCard}:hover & {
    opacity: 1;
  }
`;

const RoomLabel = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 20px 16px;
  background: linear-gradient(to top, rgba(5, 4, 3, 0.85) 0%, transparent 100%);
  opacity: 0;
  transform: translateY(6px);
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
  pointer-events: none;
`;

const RoomLabelName = styled.p`
  font-family: "Jost", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #f5ede0;
  margin: 0 0 2px 0;
`;

const RoomLabelSub = styled.p`
  font-family: "Jost", sans-serif;
  font-size: 0.58rem;
  letter-spacing: 0.14em;
  color: rgba(201, 169, 110, 0.7);
  margin: 0;
`;

const ROOMS = [
  {
    name: "Living Room",
    sub: "Large statement wall",
    bg: livingRoom,
    defaultPos: { top: "10%", left: "32%" },
  },
  {
    name: "Bedroom",
    sub: "Above the headboard",
    bg: bedroom,
    defaultPos: { top: "18%", left: "33%" },
  },
  {
    name: "Office",
    sub: "Focused workspace",
    bg: office,
    defaultPos: { top: "17%", left: "34%" },
  },
];

/* ══════════════════════════════════════════════════
   DRAGGABLE ROOM CARD sub-component
══════════════════════════════════════════════════ */
const DraggableRoomCard = ({ room, imageUrl, imageAlt }) => {
  const cardRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null); // { startX, startY, origLeft, origTop }

  // position as px offsets from card top-left
  const [pos, setPos] = useState(null); // null = use CSS % defaults on first render

  // Convert the % default into px once the card has dimensions
  const initPos = useCallback(() => {
    if (pos !== null || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const pctLeft = parseFloat(room.defaultPos.left) / 100;
    const pctTop = parseFloat(room.defaultPos.top) / 100;
    setPos({ x: rect.width * pctLeft, y: rect.height * pctTop });
  }, [pos, room.defaultPos]);

  const onMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      initPos();
      const rect = cardRef.current.getBoundingClientRect();
      const curPos = pos ?? {
        x: (rect.width * parseFloat(room.defaultPos.left)) / 100,
        y: (rect.height * parseFloat(room.defaultPos.top)) / 100,
      };
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origLeft: curPos.x,
        origTop: curPos.y,
      };
      imgRef.current?.classList.add("dragging");

      const onMove = (me) => {
        const dx = me.clientX - dragRef.current.startX;
        const dy = me.clientY - dragRef.current.startY;
        setPos({
          x: dragRef.current.origLeft + dx,
          y: dragRef.current.origTop + dy,
        });
      };
      const onUp = () => {
        imgRef.current?.classList.remove("dragging");
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [pos, initPos, room.defaultPos],
  );

  // Touch support
  const onTouchStart = useCallback(
    (e) => {
      const t = e.touches[0];
      initPos();
      const rect = cardRef.current.getBoundingClientRect();
      const curPos = pos ?? {
        x: (rect.width * parseFloat(room.defaultPos.left)) / 100,
        y: (rect.height * parseFloat(room.defaultPos.top)) / 100,
      };
      dragRef.current = {
        startX: t.clientX,
        startY: t.clientY,
        origLeft: curPos.x,
        origTop: curPos.y,
      };

      const onMove = (me) => {
        const touch = me.touches[0];
        const dx = touch.clientX - dragRef.current.startX;
        const dy = touch.clientY - dragRef.current.startY;
        setPos({
          x: dragRef.current.origLeft + dx,
          y: dragRef.current.origTop + dy,
        });
      };
      const onEnd = () => {
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("touchend", onEnd);
      };
      window.addEventListener("touchmove", onMove, { passive: true });
      window.addEventListener("touchend", onEnd);
    },
    [pos, initPos, room.defaultPos],
  );

  const imgStyle = pos
    ? { left: pos.x, top: pos.y }
    : { left: room.defaultPos.left, top: room.defaultPos.top };

  return (
    <RoomCard
      ref={cardRef}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
    >
      <RoomBg src={room.bg} alt={room.name} loading="lazy" />

      <ArtworkImg
        ref={imgRef}
        className="room-artwork"
        src={imageUrl}
        alt={imageAlt}
        draggable={false}
        style={imgStyle}
      />

      <DragHint>drag to reposition</DragHint>

      <RoomLabel className="room-label">
        <RoomLabelName>{room.name}</RoomLabelName>
        <RoomLabelSub>{room.sub}</RoomLabelSub>
      </RoomLabel>
    </RoomCard>
  );
};

/* ══════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════ */
const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFrame, setSelectedFrame] = useState(FRAME_TYPES[0]);
  const [selectedSize, setSelectedSize] = useState(SIZES[0]);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, pr] = await Promise.all([getProductById(id), getPricing()]);
        setProduct(p);
        setPricing(pr);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const currentPrice = pricing?.[selectedFrame]?.[selectedSize] ?? null;
  const priceValid = currentPrice && currentPrice > 0;

  const handleAdd = () => {
    if (!product || !priceValid) return;
    addItem({
      productId: product.id,
      name: product.name,
      imageUrl: product.imageUrl,
      category: product.category,
      frameType: selectedFrame,
      size: selectedSize,
      price: parseFloat(currentPrice),
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  if (loading) {
    return (
      <CenterWrap>
        <LoadingDot $d="0s" />
        <LoadingDot $d="0.2s" />
        <LoadingDot $d="0.4s" />
      </CenterWrap>
    );
  }

  if (!product) {
    return (
      <CenterWrap>
        <span
          style={{
            color: "rgba(245,237,224,0.3)",
            fontSize: "0.75rem",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Product not found
        </span>
      </CenterWrap>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Page>
        <Layout>
          {/* ── LEFT: Image ── */}
          <ImageSide>
            <ImageFrame>
              {product.imageUrl ? (
                <ProductImg src={product.imageUrl} alt={product.name} />
              ) : (
                <NoImage>No Image</NoImage>
              )}
            </ImageFrame>
          </ImageSide>

          {/* ── RIGHT: Info ── */}
          <InfoSide>
            <Note>
              <b>Note: </b>Images on this website are reduced in quality for
              better website experience. <br /> All prints are produced in full
              8K resolution, captured with a professional camera.
            </Note>
            <BreadCrumb onClick={() => navigate(-1)}>← Back</BreadCrumb>

            <ProductName>{product.name}</ProductName>
            <CategoryTag>{product.category}</CategoryTag>

            <PriceRow>
              {priceValid ? (
                <PriceValue>€{currentPrice.toFixed(2)}</PriceValue>
              ) : (
                <PriceValue style={{ color: "rgba(245,237,224,0.2)" }}>
                  —
                </PriceValue>
              )}
              <PriceUnit>
                {selectedFrame} · {selectedSize}
              </PriceUnit>
            </PriceRow>

            <GoldRule />

            {/* Frame type */}
            <SectionLabel>
              Material: <span>{selectedFrame}</span>
            </SectionLabel>
            <FrameGrid>
              {FRAME_TYPES.map((frame) => (
                <FrameCard
                  key={frame}
                  $active={selectedFrame === frame}
                  onClick={() => setSelectedFrame(frame)}
                >
                  <FrameCardImg $active={selectedFrame === frame}>
                    <img src={FRAME_META[frame].img} alt={frame} />
                  </FrameCardImg>
                  <FrameCardLabel $active={selectedFrame === frame}>
                    {frame}
                  </FrameCardLabel>
                </FrameCard>
              ))}
            </FrameGrid>

            {/* Size */}
            <SectionLabel>
              Dimensions: <span>{selectedSize}</span>
            </SectionLabel>
            <SizePills>
              {SIZES.map((size) => (
                <SizePill
                  key={size}
                  $active={selectedSize === size}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </SizePill>
              ))}
            </SizePills>

            {/* CTA */}
            <AddBtn onClick={handleAdd} disabled={!priceValid}>
              {priceValid ? "Add to Cart" : "Unavailable"}
            </AddBtn>

            {!priceValid && (
              <NoPriceNote>
                Price not configured for this combination yet.
              </NoPriceNote>
            )}

            <AddedBadge $visible={added}>✓ Added to cart</AddedBadge>

            {/* Frame descriptions */}
            <DescSection>
              {FRAME_TYPES.map((frame) => (
                <DescCard key={frame}>
                  <DescTitle>{frame}</DescTitle>
                  <DescText>{FRAME_META[frame].desc}</DescText>
                </DescCard>
              ))}
            </DescSection>
          </InfoSide>
        </Layout>

        {/* ── Room Preview Section ── */}
        {product.imageUrl && (
          <RoomSection>
            <RoomSectionHeader>
              <RoomEyebrow>Visualise</RoomEyebrow>
              <RoomTitle>See it on your wall</RoomTitle>
              <RoomSubtitle>
                Preview how this piece looks in different spaces — drag to
                reposition
              </RoomSubtitle>
            </RoomSectionHeader>

            <RoomGrid>
              {ROOMS.map((room) => (
                <DraggableRoomCard
                  key={room.name}
                  room={room}
                  imageUrl={product.imageUrl}
                  imageAlt={product.name}
                />
              ))}
            </RoomGrid>
          </RoomSection>
        )}
      </Page>
    </>
  );
};

export default ProductPage;
